import { useCallback, useEffect } from 'react';
import { AppState } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useQuery } from '@apollo/client/react';
import { usePowerSync } from '~/powersync/contexts/power-sync/PowerSync';
import { db } from '@/lib/local-api';
import { logger } from '@/lib/logger';
import { useHealth } from '~/health/contexts/health/Health';
import { BodyMeasurementMapper } from '~/health/lib/body-measurement/body-measurement-mapper';
import { ImportWindow } from '~/health/lib/body-measurement/import-window';
import { CurrentUserDocument } from '~/graphql/generated/graphql';

/** AsyncStorage key for the max `measuredAt` already imported (per install). */
const HIGH_WATER_MARK_KEY = 'health.bodyMeasurement.highWaterMark';

/**
 * Imports body-composition readings (weight / body-fat % / lean mass) from the
 * health store into the synced `BodyMeasurement` table. Unlike #29's
 * session import there's no workout anchor and no confirm sheet — point-in-time
 * readings are unambiguous, so import is silent and deduped by the deterministic
 * id (insert-or-ignore).
 *
 * Bounded read window: it persists a high-water mark (max `measuredAt` seen) and
 * reads `[highWaterMark, now]` on later runs, with a one-time backfill on first
 * connect. Triggers: opportunistic on mount + whenever the app returns to the
 * foreground (this hook owns the `AppState` listener), plus the returned
 * `importLatest` for a manual "Sync now".
 */
interface UseBodyMeasurementImportOptions {
  /**
   * Re-import whenever the app returns to the foreground. Default true. The
   * trend screen passes `false` so it doesn't add a second listener on top of
   * the always-mounted Profile screen — it still imports once on open.
   */
  autoImportOnForeground?: boolean;
}

export const useBodyMeasurementImport = ({
  autoImportOnForeground = true,
}: UseBodyMeasurementImportOptions = {}) => {
  // usePowerSync()'s kysely type resolves to `never` codebase-wide (see
  // useRecordedSessionImport); the runtime value is the real query builder.
  const powerSync = usePowerSync();
  const { isAvailable, isConnected, readBodyMeasurements } = useHealth();
  const { data } = useQuery(CurrentUserDocument);
  const userId = data?.currentUser?.id;

  const importLatest = useCallback(async () => {
    if (!isAvailable || !isConnected || !userId) return;

    try {
      const now = new Date();
      const storedMark = await AsyncStorage.getItem(HIGH_WATER_MARK_KEY);
      const from = ImportWindow.resolveStart(now, storedMark);

      const samples = await readBodyMeasurements({ from, to: now });
      if (samples.length === 0) return;

      let highWaterMark = 0;
      for (const sample of samples) {
        await db.BodyMeasurement.mutations.create(
          powerSync,
          BodyMeasurementMapper.toCreateInput(sample, { userId }, now),
        );
        highWaterMark = ImportWindow.nextMark(
          highWaterMark,
          sample.measuredAt.getTime(),
          now.getTime(),
        );
      }

      if (highWaterMark > 0) {
        await AsyncStorage.setItem(HIGH_WATER_MARK_KEY, String(highWaterMark));
      }
    } catch (error) {
      logger.error(error, '[Health] body-measurement import failed');
    }
  }, [isAvailable, isConnected, userId, readBodyMeasurements, powerSync]);

  useEffect(() => {
    void importLatest();
    if (!autoImportOnForeground) return;
    const subscription = AppState.addEventListener('change', (state) => {
      if (state === 'active') void importLatest();
    });
    return () => subscription.remove();
  }, [importLatest, autoImportOnForeground]);

  return { importLatest };
};
